import { ParadaItinerario } from '../../core/models/parada-itinerario.model'

/**
 * Gera a chave de identidade de uma parada — mesmo conjunto de campos
 * usado pelo backend no cálculo do CRC do tarifário:
 * `trecho|cep|logradouro|numero|cidade|estado`.
 */
export function paradaKey(parada: ParadaItinerario): string {
  const norm = (v: any) => (v ?? '').toString().trim().toLowerCase()
  return [
    norm(parada.trecho),
    norm(parada.cep),
    norm(parada.logradouro),
    norm(parada.numero),
    norm(parada.cidade),
    norm(parada.estado),
  ].join('|')
}

/**
 * Compara dois conjuntos de paradas considerando ordem e identidade.
 * Usado para decidir se a edição de paradas na proposta gera um fork.
 */
export function paradasIguais(
  a: ParadaItinerario[] = [],
  b: ParadaItinerario[] = [],
): boolean {
  if (a.length !== b.length) {
    return false
  }
  for (let i = 0; i < a.length; i++) {
    if (paradaKey(a[i]) !== paradaKey(b[i])) {
      return false
    }
  }
  return true
}

/**
 * Reescreve o campo `ordem` de cada parada com base na posição atual
 * da lista (1, 2, 3…). Retorna uma nova lista.
 */
export function reordenarParadas(
  paradas: ParadaItinerario[] = [],
): ParadaItinerario[] {
  return paradas.map((parada, index) => ({ ...parada, ordem: index + 1 }))
}

function toNumberOrNull(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const n =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(',', '.'))
  return isNaN(n) ? null : n
}

/**
 * Converte uma parada da UI no payload esperado pelo backend
 * (`TarifarioItinerarioParadaDto`): usa `localParada`, omite `pais`,
 * envia `latitude`/`longitude` como número|null e `ordem` pela posição.
 */
export function toParadaDto(parada: ParadaItinerario, index = 0): any {
  return {
    ordem: parada.ordem ?? index + 1,
    trecho: parada.trecho ?? 'P',
    areaTarifario: parada.areaTarifario ?? 'N',
    localParada: parada.localParada ?? parada.descricao ?? '',
    cep: parada.cep ?? '',
    logradouro: parada.logradouro ?? '',
    numero: parada.numero ?? '',
    bairro: parada.bairro ?? '',
    complemento: parada.complemento ?? '',
    cidade: parada.cidade ?? '',
    estado: parada.estado ?? '',
    latitude: toNumberOrNull(parada.latitude),
    longitude: toNumberOrNull(parada.longitude),
    horaParada: parada.horaParada ?? '',
    tempoPermanencia: parada.tempoPermanencia ?? '',
    atividades: parada.atividades ?? '',
  }
}

export function toParadasDto(paradas: ParadaItinerario[] = []): any[] {
  return paradas.map((parada, index) => toParadaDto(parada, index))
}

/**
 * Mantém apenas as paradas reais (trecho `P`), descartando Origem (`O`) e
 * Destino (`D`). O itinerário do tarifário (`GET /api/Tarifario/{id}/itinerario`
 * e o `paradas` da search) vem com todos os trechos O/P/D — o editor e as
 * listas de "Paradas" só devem considerar os trechos `P`.
 */
export function apenasParadasP(data: any[] = []): any[] {
  return (data ?? []).filter((p) => (p?.trecho ?? 'P') === 'P')
}

/**
 * Normaliza as paradas que chegam do backend (camelCase, com `localParada`)
 * para o formato usado no editor: garante `descricao` para exibição.
 */
export function normalizeParadasFromVm(paradas: any[] = []): ParadaItinerario[] {
  return (paradas ?? []).map((parada) => ({
    ...parada,
    descricao: parada.localParada ?? parada.descricao ?? '',
  }))
}
